import { ColumnId, CompiledView, SheetColumn, SheetSource } from "./sheetTypes";

/**
 * Column widths and the horizontal window of a virtualised grid.
 *
 * Widths are measured ONCE per sheet, from the file's own rows, and never from
 * the current view: measuring the view makes every column jump a few pixels
 * each time a filter or a sort changes what the sample contains.
 */

export const ROW_HEIGHT = 28;
export const MIN_COLUMN_WIDTH = 150;
export const MAX_COLUMN_WIDTH = 420;
/** A column dragged by hand may pass the measured cap, but not without limit. */
export const MAX_RESIZE_WIDTH = 900;
export const ROW_INDEX_WIDTH = 64;
const WIDTH_SAMPLE_ROWS = 50;
const CHAR_WIDTH = 7;
const CELL_PADDING = 24;
/** Header room for the column letter, the sort arrow and the menu caret. */
const HEADER_CONTROLS_WIDTH = 46;

/** One entry per column of the source, indexed by SheetColumn.sourceIndex. */
export function measureColumnWidths(source: SheetSource): number[] {
  const widths: number[] = new Array(source.columnCount);
  const sampleSize = Math.min(source.rows.length, WIDTH_SAMPLE_ROWS);

  for (let c = 0; c < source.columnCount; c++) {
    const column = source.columns[c];
    const headerText = column ? column.label || column.letter : "";
    let longest = headerText.length;
    for (let r = 0; r < sampleSize; r++) {
      const cell = source.rows[r].cells[c];
      if (cell == null) continue;
      if (cell.length > longest) longest = cell.length;
    }
    const contentWidth = longest * CHAR_WIDTH + CELL_PADDING;
    const headerWidth = headerText.length * CHAR_WIDTH + HEADER_CONTROLS_WIDTH;
    widths[c] = Math.min(
      Math.max(MIN_COLUMN_WIDTH, contentWidth, headerWidth),
      MAX_COLUMN_WIDTH,
    );
  }
  return widths;
}

export interface GridGeometry {
  /** Width of each VISIBLE column, in view order. */
  widths: number[];
  /** Prefix sums, so offsets[i] is where column i starts. Length is widths + 1. */
  offsets: number[];
  /** Row gutter plus every visible column. */
  totalWidth: number;
}

/**
 * Widths come from the file, and a width the person dragged wins over the
 * measurement. Overrides are keyed by column id, so they survive hiding and
 * re-showing a column.
 */
export function geometryFor(
  columns: SheetColumn[],
  widthBySourceIndex: number[],
  widthOverrides?: Record<ColumnId, number>,
  gutterWidth: number = ROW_INDEX_WIDTH,
): GridGeometry {
  const widths: number[] = new Array(columns.length);
  const offsets: number[] = new Array(columns.length + 1);
  offsets[0] = 0;
  for (let i = 0; i < columns.length; i++) {
    const override = widthOverrides ? widthOverrides[columns[i].id] : undefined;
    const width =
      override != null
        ? override
        : (widthBySourceIndex[columns[i].sourceIndex] ?? MIN_COLUMN_WIDTH);
    widths[i] = width;
    offsets[i + 1] = offsets[i] + width;
  }
  return {
    widths,
    offsets,
    totalWidth: gutterWidth + offsets[columns.length],
  };
}

/**
 * Width that fits the longest value a person can actually see. It walks every
 * row of the current view rather than a sample: a full pass over 200k rows of
 * one column costs about 3 ms, and a sampled "best effort" width would be a
 * promise the double click does not keep.
 */
export function autoFitWidth(
  view: CompiledView,
  column: SheetColumn,
  headerText: string,
): number {
  let longest = headerText.length;
  const rows = view.rows;
  for (let i = 0; i < rows.length; i++) {
    const cell = rows[i].cells[column.sourceIndex];
    if (cell == null) continue;
    if (cell.length > longest) longest = cell.length;
  }
  return Math.min(
    Math.max(MIN_COLUMN_WIDTH, longest * CHAR_WIDTH + CELL_PADDING),
    MAX_RESIZE_WIDTH,
  );
}

/**
 * How many of the visible columns are frozen. The boundary is stored as an
 * index into the RAW matrix, so hiding a column between the frozen ones and the
 * boundary cannot silently freeze a different column.
 *
 * Which columns that covers is a question about the ORDER the person is looking
 * at, not about raw indices, so the full ordered list is walked alongside the
 * visible one: the frozen block is every visible column up to the boundary's
 * place in that order. In file order this counts exactly what the old
 * arithmetic counted, including when the boundary column itself is hidden.
 */
export function frozenColumnCount(
  columns: SheetColumn[],
  throughSourceIndex: number | null,
  ordered: SheetColumn[],
): number {
  if (throughSourceIndex == null) return 0;
  let count = 0;
  for (let i = 0; i < ordered.length; i++) {
    if (count < columns.length && columns[count].id === ordered[i].id) {
      count++;
    }
    if (ordered[i].sourceIndex === throughSourceIndex) return count;
  }
  // A boundary this sheet no longer has - a stored view whose file has changed
  // shape. Freezing nothing is the honest answer.
  return 0;
}

export interface ColumnRange {
  first: number;
  /** Inclusive; -1 together with first === 0 means "no columns". */
  last: number;
}

/**
 * Which columns are worth putting in the DOM. Below the threshold the answer is
 * "all of them", which keeps narrow sheets rendering exactly as they did before
 * the window existed - the cheapest way to not regress the common case.
 */
export const COLUMN_WINDOW_THRESHOLD = 30;

export function visibleColumnRange(
  offsets: number[],
  scrollLeft: number,
  viewportWidth: number,
  overscan: number = 2,
  frozenCount: number = 0,
): ColumnRange {
  const count = offsets.length - 1;
  if (count <= 0) return { first: 0, last: -1 };
  if (count <= COLUMN_WINDOW_THRESHOLD) return { first: 0, last: count - 1 };

  // Frozen columns sit on top of the left edge, so the scrolling ones only
  // become visible past them.
  const frozen = Math.min(Math.max(frozenCount, 0), count);
  const frozenWidth = offsets[frozen];
  const left = Math.max(0, scrollLeft) + frozenWidth;
  const right = Math.max(0, scrollLeft) + Math.max(viewportWidth, 1);

  let first = binarySearchOffset(offsets, left);
  let last = binarySearchOffset(offsets, right);

  // The window never repeats a frozen column - it is already drawn.
  first = Math.max(frozen, first - overscan);
  last = Math.min(count - 1, last + overscan);
  if (last < first) last = first - 1;
  return { first, last };
}

/** Index of the column containing `x`, clamped to the existing columns. */
function binarySearchOffset(offsets: number[], x: number): number {
  let low = 0;
  let high = offsets.length - 2;
  if (high < 0) return 0;
  while (low < high) {
    const mid = (low + high + 1) >> 1;
    if (offsets[mid] <= x) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }
  return low;
}

/**
 * Where a column dropped at `viewportX` would land: an index in [0, columns],
 * where `columns` means "after the last one". Measured from the grid's own left
 * edge, so the caller subtracts the grid's bounding rect and nothing else.
 *
 * Two coordinate regimes, because the header is one strip painted in two ways:
 * the gutter and the frozen cells are pinned, so their painted x IS their
 * offset, while everything past the seam has scrolled by scrollLeft. A column
 * that is not pinned cannot be dropped underneath the pinned block either - the
 * places under it belong to columns drawn on top.
 */
export function insertionIndexAt(
  geometry: GridGeometry,
  frozenCount: number,
  scrollLeft: number,
  viewportX: number,
  gutterWidth: number = ROW_INDEX_WIDTH,
): number {
  const offsets = geometry.offsets;
  const count = offsets.length - 1;
  if (count <= 0) return 0;
  const frozen = Math.min(Math.max(frozenCount, 0), count);
  const seam = gutterWidth + offsets[frozen];
  const pinned = viewportX < seam;
  const columnX = pinned
    ? viewportX - gutterWidth
    : viewportX - gutterWidth + Math.max(0, scrollLeft);
  const k = binarySearchOffset(offsets, columnX);
  const middle = (offsets[k] + offsets[k + 1]) / 2;
  const index = columnX < middle ? k : k + 1;
  const lowest = pinned ? 0 : frozen;
  return Math.min(Math.max(index, lowest), count);
}

/**
 * What is left of a selection after the columns have moved. A cell selection is
 * a rectangle of POSITIONS, and the data under those positions has just
 * changed, so it goes - Ctrl+C, the Sum readout and "hide selected rows" all
 * act on it immediately and would otherwise speak for columns nobody selected.
 * A row selection names rows, and rows do not move when columns do.
 */
export function selectionAfterColumnsMoved(
  selection: GridSelection | null,
): GridSelection | null {
  return selection && selection.kind === "rows" ? selection : null;
}

export interface SelectionRect {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

export interface GridSelection {
  kind: "cells" | "rows";
  anchorRow: number;
  anchorCol: number;
  focusRow: number;
  focusCol: number;
}

export function selectionRect(selection: GridSelection): SelectionRect {
  return {
    top: Math.min(selection.anchorRow, selection.focusRow),
    bottom: Math.max(selection.anchorRow, selection.focusRow),
    // A row selection covers the whole row whatever its column bounds say.
    left:
      selection.kind === "rows"
        ? 0
        : Math.min(selection.anchorCol, selection.focusCol),
    right:
      selection.kind === "rows"
        ? Number.MAX_SAFE_INTEGER
        : Math.max(selection.anchorCol, selection.focusCol),
  };
}

export function isCellSelected(
  selection: GridSelection | null,
  rowPos: number,
  colPos: number,
): boolean {
  if (!selection) return false;
  const rect = selectionRect(selection);
  return (
    rowPos >= rect.top &&
    rowPos <= rect.bottom &&
    colPos >= rect.left &&
    colPos <= rect.right
  );
}
